// v640.8: Daily Dashboard — at-a-glance modal showing today's free
// Delve run, active bounties, patron status, and active quests.
import React, { useEffect, useState } from 'react';
import '../styles/Social.css';

const DailyDashboard = ({ onClose, onOpenBounties, onOpenDelve, onOpenCompendium, onOpenCrownShop }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/player/daily-status', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load');
        setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fmtUntil = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return null; }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, padding: 18 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0, color: '#c9a84c', fontSize: 20 }}>📅 Daily Dashboard</h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#888', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
            title="Close (Esc)"
          >×</button>
        </div>

        {loading && <div style={{ color: '#999', padding: '20px 0', textAlign: 'center' }}>Loading…</div>}
        {error && <div style={{ color: '#e74c3c', padding: '20px 0', textAlign: 'center' }}>{error}</div>}

        {data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Free Delve */}
            <DashCard
              title="🗝️ Free Delve Run"
              accent="#10b981"
              statusText={
                data.delve.freeRemaining > 0
                  ? `${data.delve.freeRemaining}/${data.delve.freeMax} available today`
                  : `Used ${data.delve.freeUsed}/${data.delve.freeMax} — resets at UTC midnight`
              }
              statusGood={data.delve.freeRemaining > 0}
              detail={data.delve.streak > 0 ? `🔥 ${data.delve.streak}-day streak` : null}
              actionLabel={data.delve.freeRemaining > 0 ? 'Open Delve' : null}
              onAction={onOpenDelve}
            />

            {/* Bounties */}
            <DashCard
              title="🎯 Bounties"
              accent="#f59e0b"
              statusText={
                data.bounties.total === 0
                  ? 'No bounties accepted — visit a tavern board'
                  : `${data.bounties.active} in progress · ${data.bounties.claimable} ready to claim`
              }
              statusGood={data.bounties.claimable > 0 || data.bounties.active > 0}
              detail={
                data.bounties.activeBounties && data.bounties.activeBounties.length
                  ? data.bounties.activeBounties.slice(0, 3).map((b, i) => (
                      <div key={i} style={{ fontSize: 11, color: '#aaa' }}>
                        {b.complete ? '✓' : '•'} {b.monster}: {b.current}/{b.target} <span style={{ color: '#666' }}>({b.tavern})</span>
                      </div>
                    ))
                  : null
              }
              actionLabel={data.bounties.total > 0 ? 'View Active' : null}
              onAction={onOpenBounties}
            />

            {/* Quests */}
            <DashCard
              title="📜 Quests"
              accent="#60a5fa"
              statusText={
                data.quests.active === 0 && data.quests.completed === 0
                  ? 'No quests yet — explore towns to find quest-givers'
                  : `${data.quests.active} active · ${data.quests.completed} completed`
              }
              statusGood={data.quests.active > 0}
              actionLabel={data.quests.total > 0 ? 'Open Compendium' : null}
              onAction={onOpenCompendium}
            />

            {/* Patron */}
            <DashCard
              title={data.patron.isPatron ? '👑 Patron of the Realm' : '👑 Patron Status'}
              accent={data.patron.isPatron ? '#c9a84c' : '#666'}
              statusText={
                data.patron.isPatron
                  ? `Active${data.patron.until ? ` · renews/expires ${fmtUntil(data.patron.until)}` : ''} · 200 Crowns/month grant + 2 free Delve runs`
                  : 'Not a Patron — subscribe for monthly Crowns, 2 free Delve runs, 10% Crown Shop discount'
              }
              statusGood={data.patron.isPatron}
              actionLabel={data.patron.isPatron ? null : 'Visit Crown Shop'}
              onAction={onOpenCrownShop}
            />

            <div style={{ fontSize: 10, color: '#555', textAlign: 'center', marginTop: 4 }}>
              Refreshes daily at 00:00 UTC · Generated {new Date(data.generatedAt).toLocaleTimeString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DashCard = ({ title, accent, statusText, statusGood, detail, actionLabel, onAction }) => (
  <div style={{
    background: 'rgba(20, 20, 28, 0.7)',
    border: `1px solid ${accent}33`,
    borderLeft: `3px solid ${accent}`,
    borderRadius: 6,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: accent, fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 12, color: statusGood ? '#dcd0a0' : '#888', lineHeight: 1.4 }}>{statusText}</div>
      {detail && <div style={{ marginTop: 4 }}>{detail}</div>}
    </div>
    {actionLabel && onAction && (
      <button
        onClick={onAction}
        style={{
          background: `${accent}22`,
          border: `1px solid ${accent}66`,
          color: accent,
          padding: '4px 10px',
          borderRadius: 4,
          fontSize: 11,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >{actionLabel}</button>
    )}
  </div>
);

export default DailyDashboard;
